import { useState } from "react";
import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Search } from "lucide-react";

const faqs = [
  { q: "What exactly happens if a guard takes a job and never shows up?", a: "The venue reports the no-show in-app at shift start time. We verify with GPS data (the guard never checked in) and contact the guard once. If the no-show is confirmed without a documented emergency, the account is permanently banned and a replacement guard is auto-dispatched to the venue. This is our one zero-tolerance rule." },
  { q: "Is there any appeal process for a ban?", a: "Documented emergencies (hospital records, police reports) can be submitted within 72 hours for review. 'I forgot' or 'something came up' are not emergencies. The rule is strict because it's the entire foundation of trust on the platform." },
  { q: "How do guards actually get paid?", a: "Directly by the venue, at the rate posted on the shift. GuardGig28 never touches your wages — no agency cut, no invoicing games. Your GPS checkout is the proof the hours happened, which is what settles any dispute." },
  { q: "How does GuardGig28 verify licences?", a: "SIA licence numbers are checked against the Security Industry Authority register at signup, and re-verified continuously. A lapsed or revoked licence suspends the account automatically until it's renewed and re-verified." },
  { q: "What if a guard cancels an hour before the shift?", a: "Late cancellations (under 4 hours) are treated as strikes: two strikes in 90 days equals the same permanent ban as a no-show. Cancel earlier than 4 hours and there's no penalty — the shift simply returns to the pool." },
  { q: "What does it cost venues to post a shift?", a: "Venues pay the guard's hourly rate plus a flat platform fee per filled shift. No subscriptions, no minimums. You only pay when a verified guard actually checks in." },
];

export default function Faq() {
  const [query, setQuery] = useState("");
  const filtered = faqs.filter(
    (f) => f.q.toLowerCase().includes(query.toLowerCase()) || f.a.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <section id="faq" data-testid="faq-section" className="py-24 md:py-32">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono-tag text-[11px] text-amber-500">Straight answers</p>
          <h2 className="font-display mt-4 text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl">
            The tough questions
          </h2>

          <div className="relative mt-10">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="faq-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the rules — try 'no-show' or 'paid'"
              className="h-13 w-full rounded-md border border-white/10 bg-[#0f141c] py-4 pl-11 pr-4 text-sm text-slate-50 placeholder:text-slate-600 focus:border-amber-500/60 focus:outline-none"
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-8"
        >
          {filtered.length === 0 ? (
            <p data-testid="faq-empty" className="py-10 text-center font-mono-tag text-[11px] text-slate-600">No matches — try another keyword</p>
          ) : (
            <Accordion type="single" collapsible className="space-y-3">
              {filtered.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="rounded-lg border border-white/8 bg-[#0f141c] px-6 transition-colors data-[state=open]:border-amber-500/30"
                >
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className="py-5 text-left font-display text-lg font-bold uppercase text-slate-100 hover:text-amber-500 hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-sm leading-relaxed text-slate-400">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </motion.div>
      </div>
    </section>
  );
}
